import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { consultationService } from '../services/consultationService'
import { Consultation } from '../types/consultation'
import { ConsultationForm } from '../components/ConsultationForm'
import { ConsultationArtefactList } from '../components/ConsultationArtefactList'
import { CreateArtefactForm } from '../components/CreateArtefactForm'
import { Modal } from '../components/Modal'
import { VitalSignsList } from '../components/VitalSignsList'
import { formatDate } from '../utils/locale'
import { getUnitPreference } from '../utils/userPreferences'
import { taskService } from '../services/taskService'
import { TaskForm } from '../components/TaskForm'
import './ConsultationDetail.css'

export function ConsultationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showEditForm, setShowEditForm] = useState(false)
  const [showCreateArtefact, setShowCreateArtefact] = useState(false)
  const [showCreateTask, setShowCreateTask] = useState(false)

  const { data: consultation, isLoading, error } = useQuery({
    queryKey: ['consultation', id],
    queryFn: () => consultationService.getById(id!),
    enabled: !!id,
  })

  // Redirect in-progress consultations to workspace
  useEffect(() => {
    if (consultation && consultation.status === 'in-progress' && id) {
      navigate(`/consultations/${id}/workspace`, { replace: true })
    }
  }, [consultation, id, navigate])

  const deleteMutation = useMutation({
    mutationFn: () => consultationService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consultations'] })
      if (consultation?.patientId) {
        navigate(`/patients/${consultation.patientId}`)
      } else {
        navigate('/patients')
      }
    },
  })

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this consultation? This action cannot be undone.')) {
      try {
        await deleteMutation.mutateAsync()
      } catch (error) {
        alert('Failed to delete consultation')
      }
    }
  }

  if (isLoading) return <div className="loading">Loading consultation...</div>
  if (error) return <div className="error">Error loading consultation</div>
  if (!consultation) return <div>Consultation not found</div>

  // Show loading state while redirecting in-progress consultations
  if (consultation.status === 'in-progress') {
    return <div className="loading">Redirecting to workspace...</div>
  }

  const getStatusBadge = (status: Consultation['status']) => {
    const statusClasses = {
      'in-progress': 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
      'no-show': 'status-no-show',
    }
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>
  }

  return (
    <div className="consultation-detail">
      <div className="consultation-detail-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate(`/patients/${consultation.patientId}`)}>
            ← Back to Patient
          </button>
          <div>
            <h1>Consultation</h1>
            <div className="consultation-meta">
              <span className="consultation-date">
                {formatDate(consultation.consultationDate)}
                {consultation.startTime && ` at ${consultation.startTime}`}
                {consultation.endTime && ` - ${consultation.endTime}`}
              </span>
              {getStatusBadge(consultation.status)}
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => setShowCreateTask(true)}>
            + Create Task
          </button>
          <button className="btn-secondary" onClick={() => setShowCreateArtefact(true)}>
            + Add Artefact
          </button>
          <button className="btn-secondary" onClick={() => setShowEditForm(true)}>
            Edit
          </button>
          <button className="btn-danger" onClick={handleDelete} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      <div className="consultation-content">
        <div className="consultation-main">
          <div className="consultation-section">
            <h2>Clinical Information</h2>
            <div className="info-grid">
              {consultation.staffName && (
                <div className="info-item">
                  <label>Provider</label>
                  <p>{consultation.staffName}</p>
                </div>
              )}
              {consultation.reason && (
                <div className="info-item full-width">
                  <label>Reason for Visit</label>
                  <p>{consultation.reason}</p>
                </div>
              )}
              {consultation.diagnosis && consultation.diagnosis.length > 0 && (
                <div className="info-item full-width">
                  <label>Diagnosis</label>
                  <div className="diagnosis-list">
                    {consultation.diagnosis.map((diag, index) => (
                      <span key={index} className="diagnosis-tag">
                        {diag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {consultation.codes && consultation.codes.length > 0 && (
                <div className="info-item full-width">
                  <label>Coded Diagnoses</label>
                  <div className="diagnosis-list">
                    {consultation.codes.map((code, index) => (
                      <span key={`${code.system}-${code.code}-${index}`} className="diagnosis-tag">
                        {code.system}:{code.code} – {code.display}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {consultation.notes && (
                <div className="info-item full-width">
                  <label>Clinical Notes</label>
                  <div className="notes-content">{consultation.notes}</div>
                </div>
              )}
              {consultation.plan && (
                <div className="info-item full-width">
                  <label>Treatment Plan</label>
                  <div className="plan-content">{consultation.plan}</div>
                </div>
              )}
              {consultation.followUpDate && (
                <div className="info-item">
                  <label>Follow-up Date</label>
                  <p>{formatDate(consultation.followUpDate)}</p>
                </div>
              )}
              {consultation.followUpNotes && (
                <div className="info-item full-width">
                  <label>Follow-up Notes</label>
                  <div className="notes-content">{consultation.followUpNotes}</div>
                </div>
              )}
              {(consultation.patientInstructions || consultation.instructionsGiven) && (
                <div className="info-item full-width consultation-instructions-section">
                  <label>Patient Instructions</label>
                  {consultation.patientInstructions && (
                    <div className="instructions-content">{consultation.patientInstructions}</div>
                  )}
                  {consultation.instructionsGiven && (
                    <div className="instructions-meta">
                      <span className="instructions-given-badge">Instructions Given</span>
                      {consultation.instructionsGivenAt && (
                        <span className="instructions-timestamp">
                          Given on {new Date(consultation.instructionsGivenAt).toLocaleString()}
                        </span>
                      )}
                      {consultation.instructionsGivenBy && (
                        <span className="instructions-given-by">
                          by {consultation.instructionsGivenBy}
                        </span>
                      )}
                    </div>
                  )}
                  {consultation.instructionDocumentIds && consultation.instructionDocumentIds.length > 0 && (
                    <div className="instructions-documents">
                      <label>Linked Documents:</label>
                      <ul>
                        {consultation.instructionDocumentIds.map((docId) => (
                          <li key={docId}>{docId}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="consultation-section">
            <div className="section-header">
              <h2>Vital Signs</h2>
            </div>
            <VitalSignsList
              patientId={consultation.patientId}
              consultationId={consultation.id}
              userPreference={getUnitPreference()}
            />
          </div>

          <div className="consultation-section">
            <div className="section-header">
              <h2>Artefacts</h2>
              <button className="btn-primary" onClick={() => setShowCreateArtefact(true)}>
                + Add Artefact
              </button>
            </div>
            <ConsultationArtefactList
              consultationId={consultation.id}
              artefacts={consultation.artefacts || []}
            />
          </div>
        </div>

        <div className="consultation-sidebar">
          <div className="consultation-section">
            <h3>Consultation Details</h3>
            <div className="detail-list">
              <div className="detail-item">
                <label>Status</label>
                <p>{getStatusBadge(consultation.status)}</p>
              </div>
              {consultation.duration && (
                <div className="detail-item">
                  <label>Duration</label>
                  <p>{consultation.duration} minutes</p>
                </div>
              )}
              <div className="detail-item">
                <label>Created</label>
                <p>{new Date(consultation.createdAt).toLocaleString()}</p>
              </div>
              <div className="detail-item">
                <label>Last Updated</label>
                <p>{new Date(consultation.updatedAt).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showEditForm}
        onClose={() => setShowEditForm(false)}
        title="Edit Consultation"
        size="large"
      >
        <ConsultationForm
          patientId={consultation.patientId}
          consultation={consultation}
          onSuccess={() => {
            setShowEditForm(false)
            queryClient.invalidateQueries({ queryKey: ['consultation', id] })
          }}
          onCancel={() => setShowEditForm(false)}
        />
      </Modal>

      <Modal
        isOpen={showCreateArtefact}
        onClose={() => setShowCreateArtefact(false)}
        title="Create Artefact"
        size="medium"
      >
        <CreateArtefactForm
          consultationId={consultation.id}
          onSuccess={() => {
            setShowCreateArtefact(false)
            queryClient.invalidateQueries({ queryKey: ['consultation', id] })
          }}
          onCancel={() => setShowCreateArtefact(false)}
        />
      </Modal>

      <Modal
        isOpen={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        title="Create Task"
        size="medium"
      >
        <TaskForm
          patientId={consultation.patientId}
          ehrComponentType="consultation"
          ehrComponentId={consultation.id}
          onSubmit={async (data) => {
            await taskService.createForPatient(consultation.patientId, data)
            queryClient.invalidateQueries({ queryKey: ['tasks'] })
            setShowCreateTask(false)
          }}
          onCancel={() => setShowCreateTask(false)}
        />
      </Modal>
    </div>
  )
}

